import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faChevronLeft, faChevronRight, faCircleInfo, faFolderOpen, faLink} from '@fortawesome/pro-solid-svg-icons';
import {BicButtonComponent, BicInputComponent, BicPopupComponent} from '@buildinginformationcloud/bic-ui-components';
import {
  SourceDriveView,
  SourceItemView,
  SourceLinkControllerService,
  SourceLinkView,
  SourceSiteView
} from '../../../../api';
import {NotificationsService} from '../../../../services/notifications/notifications.service';

export interface LinkSourceFolderDialogData {
  projectId: number;
  projectName: string;
  /** planBIC-Ordner, in dem der Zielordner angelegt wird (null = Projektwurzel). */
  parentFolderId: string | null;
  parentFolderName: string;
}

interface Crumb {
  id: string | null;
  name: string;
}

/** Office fasst XLSX, DOCX und PPTX zusammen (Figma Popup 6). */
const OFFICE_TYPES = ['EXCEL', 'WORD', 'POWERPOINT'];

/**
 * Ordner mit SharePoint verknüpfen (BIC-Frontend#2720, Figma Popups 4–6): Quelle (zugeordnete Site +
 * Bibliothek) → Ordner → Ziel & Optionen. Das Backend prüft Zuordnung und Bibliothek erneut; hier wird nur
 * angeboten, was es geliefert hat. Schließt mit dem angelegten {@link SourceLinkView}.
 */
@Component({
  selector: 'app-link-source-folder-dialog',
  templateUrl: './link-source-folder-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BicPopupComponent, BicButtonComponent, BicInputComponent, FaIconComponent, FormsModule]
})
export class LinkSourceFolderDialogComponent implements OnInit {
  readonly data = inject<LinkSourceFolderDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject<MatDialogRef<LinkSourceFolderDialogComponent, SourceLinkView>>(MatDialogRef);
  private readonly sourceLinks = inject(SourceLinkControllerService);
  private readonly notifications = inject(NotificationsService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly steps = ['Quelle', 'Ordner', 'Ziel & Optionen'];
  step = 0;
  loading = true;
  busy = false;

  sites: SourceSiteView[] = [];
  drives: SourceDriveView[] = [];
  siteId: string | null = null;
  driveId: string | null = null;

  /** Pfad im Ordner-Browser; das letzte Element ist der angezeigte Ordner. */
  crumbs: Crumb[] = [];
  folders: SourceItemView[] = [];
  fileCount = 0;
  /** Gewählter Quellordner; Standard ist der gerade angezeigte. */
  selected: Crumb | null = null;
  selectedChildCount: number | null = null;

  targetFolderName = '';
  pdf = true;
  ifc = true;
  office = false;

  protected readonly faChevronRight = faChevronRight;
  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faFolderOpen = faFolderOpen;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faLink = faLink;

  ngOnInit(): void {
    this.sourceLinks.listSourceSites(this.data.projectId).subscribe({
      next: sites => {
        this.sites = sites;
        this.loading = false;
        if (sites.length === 1) {
          this.selectSite(sites[0].connectionSiteId!);
        }
        this.cdr.markForCheck();
      },
      error: err => this.fail(err, 'Die zugeordneten Sites konnten nicht geladen werden.')
    });
  }

  get site(): SourceSiteView | undefined {
    return this.sites.find(s => s.connectionSiteId === this.siteId);
  }

  get drive(): SourceDriveView | undefined {
    return this.drives.find(d => d.driveId === this.driveId);
  }

  get canContinue(): boolean {
    switch (this.step) {
      case 0:
        return !!this.siteId && !!this.driveId;
      case 1:
        return !!this.selected;
      default:
        return !!this.targetFolderName.trim() && (this.pdf || this.ifc || this.office);
    }
  }

  selectSite(siteId: string): void {
    this.siteId = siteId;
    this.driveId = null;
    this.drives = [];
    this.loading = true;
    this.sourceLinks.listSourceDrives(this.data.projectId, siteId).subscribe({
      next: drives => {
        this.drives = drives;
        this.driveId = drives.length === 1 ? drives[0].driveId! : null;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: err => this.fail(err, 'Die Bibliotheken der Site konnten nicht geladen werden.')
    });
  }

  next(): void {
    if (!this.canContinue) {
      return;
    }
    if (this.step === 0) {
      this.step = 1;
      this.crumbs = [{id: null, name: this.drive?.name ?? 'Bibliothek'}];
      this.browse();
      return;
    }
    if (this.step === 1) {
      this.step = 2;
      if (!this.targetFolderName) {
        this.targetFolderName = this.selected?.name ?? '';
      }
      return;
    }
    this.create();
  }

  back(): void {
    this.step = Math.max(0, this.step - 1);
  }

  /** Ordner öffnen (Chevron): eine Ebene tiefer, der geöffnete Ordner wird zur Auswahl. */
  open(folder: SourceItemView): void {
    this.crumbs = [...this.crumbs, {id: folder.itemId!, name: folder.name!}];
    this.browse();
  }

  /** Ordner wählen (Klick auf die Zeile), ohne hineinzugehen. */
  choose(folder: SourceItemView): void {
    this.selected = {id: folder.itemId!, name: folder.name!};
    this.selectedChildCount = folder.childCount ?? null;
  }

  goTo(index: number): void {
    this.crumbs = this.crumbs.slice(0, index + 1);
    this.browse();
  }

  isSelected(folder: SourceItemView): boolean {
    return this.selected?.id === folder.itemId;
  }

  private browse(): void {
    const current = this.crumbs[this.crumbs.length - 1];
    this.loading = true;
    this.folders = [];
    // Der angezeigte Ordner ist die Vorauswahl; die Bibliothekswurzel heißt in Graph "root".
    this.selected = current.id ? current : {id: 'root', name: this.drive?.name ?? 'Bibliothek'};
    this.selectedChildCount = null;
    this.sourceLinks.listSourceChildren(this.data.projectId, this.siteId!, this.driveId!, current.id ?? undefined).subscribe({
      next: items => {
        this.folders = items.filter(i => i.folder);
        this.fileCount = items.length - this.folders.length;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: err => this.fail(err, 'Der Ordner konnte nicht geladen werden.')
    });
  }

  private create(): void {
    const fileTypes = [...(this.pdf ? ['PDF'] : []), ...(this.ifc ? ['IFC'] : []), ...(this.office ? OFFICE_TYPES : [])];
    this.busy = true;
    this.sourceLinks.createSourceLink({
      connectionSiteId: this.siteId!,
      driveId: this.driveId!,
      folderItemId: this.selected!.id!,
      targetParentFolderId: this.data.parentFolderId ?? undefined,
      targetFolderName: this.targetFolderName.trim(),
      fileTypes
    }, this.data.projectId).subscribe({
      next: link => {
        this.notifications.showSuccessMessage(`„${this.targetFolderName.trim()}“ ist verknüpft. Der erste Abgleich startet gleich.`);
        this.dialogRef.close(link);
      },
      error: err => this.fail(err, 'Der Ordner konnte nicht verknüpft werden.')
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private fail(err: { error?: { message?: string } }, fallback: string): void {
    this.loading = false;
    this.busy = false;
    this.notifications.showErrorMessage(err?.error?.message ?? fallback);
    this.cdr.markForCheck();
  }
}
